import { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Footer from '@/components/feature/Footer';
import DonationPanel from './components/DonationPanel';
import CampaignCard from '@/components/feature/CampaignCard';
import SAR from '@/components/feature/SAR';
import { campaigns } from '@/mocks/campaigns';


const mockDonors = [
  { name: 'عبدالرحمن الغامدي', amount: 500, message: 'بالتوفيق، قضية تستحق الدعم', time: 'منذ ساعتين', avatar: 'https://readdy.ai/api/search-image?query=Saudi%20man%20portrait%20clean%20background&width=60&height=60&seq=601&orientation=squarish' },
  { name: 'داعم مجهول', amount: 200, message: '', time: 'منذ 5 ساعات', avatar: '' },
  { name: 'منى الشهري', amount: 1000, message: 'جزاكم الله خيراً على هذا العمل الرائع', time: 'منذ يوم', avatar: 'https://readdy.ai/api/search-image?query=Saudi%20woman%20hijab%20portrait%20clean%20background&width=60&height=60&seq=602&orientation=squarish' },
  { name: 'فهد العنزي', amount: 300, message: 'ادعم هذه القضية بكل قلبي', time: 'منذ يومين', avatar: 'https://readdy.ai/api/search-image?query=Saudi%20man%20portrait%20professional%20clean%20background&width=60&height=60&seq=603&orientation=squarish' },
  { name: 'داعم مجهول', amount: 150, message: 'في سبيل الله', time: 'منذ 3 أيام', avatar: '' },
];

const CampaignPage = () => {
  const { id } = useParams<{ id: string }>();
  const campaign = campaigns.find((c) => c.id === id) || campaigns[0];
  const related = campaigns.filter((c) => c.id !== campaign.id && c.category === campaign.category).slice(0, 3);
  const [activeTab, setActiveTab] = useState<'story' | 'updates' | 'donors' | 'impact'>('story');
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareRef = useRef<HTMLDivElement>(null);

  const formatAmount = (val: number) => val;


  const progress = Math.min((campaign.raisedAmount / campaign.goalAmount) * 100, 100);

  const campaignUrl = `${window.location.origin}/campaign/${campaign.id}`;
  const shareText = `ادعم حملة "${campaign.title}" على منصة هِبة 🤝`;

  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + campaignUrl)}`,
    twitter:  `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(campaignUrl)}`,
    snapchat: `https://www.snapchat.com/scan?attachmentUrl=${encodeURIComponent(campaignUrl)}`,
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(campaignUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Close share dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (shareRef.current && !shareRef.current.contains(e.target as Node)) {
        setShareOpen(false);
      }
    };
    if (shareOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [shareOpen]);

  return (
    <div className="min-h-screen bg-stone-50" dir="rtl">

      {/* Breadcrumb */}
      <div className="bg-white border-b border-stone-100 pt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-3 flex items-center gap-2 text-xs text-stone-400">
          <Link to="/" className="hover:text-emerald-600 cursor-pointer">الرئيسية</Link>
          <i className="ri-arrow-left-s-line"></i>
          <Link to="/explore" className="hover:text-emerald-600 cursor-pointer">الحملات</Link>
          <i className="ri-arrow-left-s-line"></i>
          <span className="text-stone-600 line-clamp-1">{campaign.title}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Cover Image */}
            <div className="relative rounded-2xl overflow-hidden mb-6 h-64 md:h-96">
              <img
                src={campaign.coverImage}
                alt={campaign.title}
                className="w-full h-full object-cover object-top"
              />
              {/* Badges */}
              <div className="absolute top-4 right-4 flex gap-2">
                {campaign.isLicensed && (
                  <span className="bg-emerald-600 text-white text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                    <i className="ri-shield-check-line text-xs"></i>
                    حملة مرخصة
                  </span>
                )}
                {campaign.isVerified && (
                  <span className="bg-white/90 text-stone-700 text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                    <i className="ri-verified-badge-line text-emerald-600 text-xs"></i>
                    موثقة
                  </span>
                )}
              </div>
              <div className="absolute top-4 left-4">
                <span className={`text-xs px-3 py-1.5 rounded-full font-medium ${campaign.type === 'charity' ? 'bg-amber-500/90 text-white' : 'bg-stone-800/80 text-white'}`}>
                  {campaign.type === 'charity' ? 'تبرع خيري' : 'دعم شخصي'}
                </span>
              </div>
            </div>

            {/* Title & Creator */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6 mb-5">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <span className="text-emerald-600 text-xs font-semibold">{campaign.category}</span>
                  <h1 className="text-2xl md:text-3xl font-black text-stone-800 mt-1 leading-tight">
                    {campaign.title}
                  </h1>
                </div>
                {/* Share Button */}
                <div className="relative" ref={shareRef}>
                  <button
                    onClick={() => setShareOpen(!shareOpen)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      shareOpen
                        ? 'bg-emerald-600 text-white'
                        : 'bg-stone-100 hover:bg-stone-200 text-stone-700'
                    }`}
                  >
                    <i className="ri-share-forward-line text-base"></i>
                    <span className="hidden sm:inline">مشاركة</span>
                  </button>

                  {shareOpen && (
                    <div className="absolute left-0 top-12 bg-white rounded-2xl border border-stone-100 z-30 w-56 overflow-hidden">
                      {/* Header */}
                      <div className="px-4 py-3 border-b border-stone-100">
                        <p className="text-xs font-bold text-stone-700">شارك هذه الحملة</p>
                        <p className="text-xs text-stone-400 mt-0.5 truncate">{campaignUrl}</p>
                      </div>

                      {/* Share options */}
                      <div className="p-2">
                        <a
                          href={shareLinks.whatsapp}
                          target="_blank"
                          rel="nofollow noreferrer"
                          onClick={() => setShareOpen(false)}
                          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl hover:bg-stone-50 transition-colors cursor-pointer"
                        >
                          <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-green-50 shrink-0">
                            <i className="ri-whatsapp-line text-green-600 text-base"></i>
                          </div>
                          <span className="text-sm font-medium text-stone-700">واتساب</span>
                          <i className="ri-external-link-line text-stone-300 text-xs mr-auto"></i>
                        </a>

                        <a
                          href={shareLinks.twitter}
                          target="_blank"
                          rel="nofollow noreferrer"
                          onClick={() => setShareOpen(false)}
                          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl hover:bg-stone-50 transition-colors cursor-pointer"
                        >
                          <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-stone-100 shrink-0">
                            <i className="ri-twitter-x-line text-stone-800 text-base"></i>
                          </div>
                          <span className="text-sm font-medium text-stone-700">تويتر / X</span>
                          <i className="ri-external-link-line text-stone-300 text-xs mr-auto"></i>
                        </a>

                        <a
                          href={shareLinks.snapchat}
                          target="_blank"
                          rel="nofollow noreferrer"
                          onClick={() => setShareOpen(false)}
                          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl hover:bg-stone-50 transition-colors cursor-pointer"
                        >
                          <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-yellow-50 shrink-0">
                            <i className="ri-snapchat-line text-yellow-500 text-base"></i>
                          </div>
                          <span className="text-sm font-medium text-stone-700">سناب شات</span>
                          <i className="ri-external-link-line text-stone-300 text-xs mr-auto"></i>
                        </a>

                        {/* Divider */}
                        <div className="h-px bg-stone-100 my-1.5 mx-2"></div>

                        {/* Copy link */}
                        <button
                          onClick={handleCopy}
                          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl hover:bg-stone-50 transition-colors cursor-pointer"
                        >
                          <div className={`w-8 h-8 flex items-center justify-center rounded-xl shrink-0 transition-colors ${copied ? 'bg-emerald-50' : 'bg-stone-100'}`}>
                            <i className={`text-base transition-colors ${copied ? 'ri-check-line text-emerald-600' : 'ri-link text-stone-600'}`}></i>
                          </div>
                          <span className={`text-sm font-medium transition-colors ${copied ? 'text-emerald-600' : 'text-stone-700'}`}>
                            {copied ? 'تم النسخ!' : 'نسخ الرابط'}
                          </span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Creator Info */}
              <div className="flex items-center gap-3 pb-4 border-b border-stone-100">
                {campaign.creatorUsername ? (
                  <Link to={`/profile/${campaign.creatorUsername}`} className="flex items-center gap-2 group cursor-pointer">
                    <img src={campaign.creatorAvatar} alt={campaign.creatorName} className="w-10 h-10 rounded-full object-cover object-top" />
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-stone-800 text-sm group-hover:text-emerald-600 transition-colors">{campaign.creatorName}</span>
                        {campaign.isVerified && <i className="ri-verified-badge-line text-emerald-600 text-sm"></i>}
                      </div>
                      <div className="flex items-center gap-1 text-stone-400 text-xs">
                        <i className="ri-map-pin-line text-xs"></i>
                        <span>{campaign.location}</span>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="flex items-center gap-2">
                    <img src={campaign.creatorAvatar} alt={campaign.creatorName} className="w-10 h-10 rounded-full object-cover object-top" />
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-stone-800 text-sm">{campaign.creatorName}</span>
                        {campaign.isVerified && <i className="ri-verified-badge-line text-emerald-600 text-sm"></i>}
                      </div>
                      <div className="flex items-center gap-1 text-stone-400 text-xs">
                        <i className="ri-map-pin-line text-xs"></i>
                        <span>{campaign.location}</span>
                      </div>
                    </div>
                  </div>
                )}
                <div className="mr-auto flex gap-2">
                  {campaign.tags.map((tag) => (
                    <span key={tag} className="bg-stone-100 text-stone-600 text-xs px-2.5 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>

              {/* Share Bar */}
              <div className="mt-4 pt-4 border-t border-stone-100 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-stone-400 font-medium ml-1">شارك الحملة:</span>
                <a
                  href={shareLinks.whatsapp}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 hover:bg-green-100 text-green-700 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-sm"></i>
                  واتساب
                </a>
                <a
                  href={shareLinks.twitter}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-twitter-x-line text-sm"></i>
                  تويتر
                </a>
                <a
                  href={shareLinks.snapchat}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-50 hover:bg-yellow-100 text-yellow-700 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-snapchat-line text-sm"></i>
                  سناب شات
                </a>
                <button
                  onClick={handleCopy}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    copied
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-stone-100 hover:bg-stone-200 text-stone-600'
                  }`}
                >
                  <i className={`text-sm ${copied ? 'ri-check-line' : 'ri-link'}`}></i>
                  {copied ? 'تم النسخ!' : 'نسخ الرابط'}
                </button>
              </div>

              {/* Mobile Progress */}
              <div className="mt-4 lg:hidden">
                <div className="flex items-end justify-between mb-2">
                  <span className="text-xl font-black text-stone-800"><SAR amount={campaign.raisedAmount} iconSize={16} /></span>
                  <span className="text-emerald-600 font-bold text-sm">{Math.round(progress)}%</span>
                </div>
                <div className="h-2 bg-stone-100 rounded-full overflow-hidden mb-2">
                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${progress}%` }} />
                </div>
                <div className="flex justify-between text-xs text-stone-400">
                  <span>من <SAR amount={campaign.goalAmount} iconSize={11} /></span>
                  <span>{campaign.daysLeft} يوم متبقي</span>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden mb-5">
              <div className="flex border-b border-stone-100 overflow-x-auto">
                {[
                  { id: 'story', label: 'قصة الحملة', icon: 'ri-file-text-line' },
                  { id: 'updates', label: 'التحديثات', icon: 'ri-notification-3-line' },
                  { id: 'donors', label: 'الداعمون', icon: 'ri-user-heart-line' },
                  ...(campaign.impactReport ? [{ id: 'impact', label: 'تقرير الأثر', icon: 'ri-bar-chart-2-line' }] : []),
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as typeof activeTab)}
                    className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'text-emerald-600 border-b-2 border-emerald-600'
                        : 'text-stone-500 hover:text-stone-700'
                    }`}
                  >
                    <i className={`${tab.icon} text-sm`}></i>
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {activeTab === 'story' && (
                  <div className="prose prose-stone max-w-none">
                    <p className="text-stone-600 leading-relaxed text-sm mb-4">{campaign.description}</p>
                    <p className="text-stone-600 leading-relaxed text-sm mb-4">
                      نسعى من خلال هذه الحملة إلى تحقيق هدف حقيقي يخدم المجتمع ويُحدث فرقاً ملموساً في حياة الناس.
                      كل ريال تتبرع به يُقربنا خطوة نحو تحقيق هذا الحلم.
                    </p>
                    <h3 className="font-bold text-stone-800 mb-3">كيف سيُستخدم التمويل؟</h3>
                    <ul className="space-y-2">
                      {['40% - التطوير والإنتاج', '30% - التسويق والانتشار', '20% - التشغيل والإدارة', '10% - الاحتياطي'].map((item) => (
                        <li key={item} className="flex items-center gap-2 text-sm text-stone-600">
                          <i className="ri-checkbox-circle-line text-emerald-500"></i>
                          {item}
                        </li>
                      ))}
                    </ul>

                    {/* Endorsements Section */}
                    {campaign.endorsements && campaign.endorsements.length > 0 && (
                      <div className="mt-8 not-prose">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-xl">
                            <i className="ri-medal-line text-amber-500 text-base"></i>
                          </div>
                          <div>
                            <h3 className="font-bold text-stone-800 text-sm">تزكيات الخبراء</h3>
                            <p className="text-xs text-stone-400">أشخاص موثوقون راجعوا الحملة وزكّوها</p>
                          </div>
                        </div>
                        <div className="space-y-3">
                          {campaign.endorsements.map((endorsement) => {
                            const specialtyConfig = {
                              academic:     { label: 'أكاديمي', icon: 'ri-graduation-cap-line', color: 'bg-sky-50 text-sky-600 border-sky-100' },
                              entrepreneur: { label: 'رائد أعمال', icon: 'ri-rocket-line', color: 'bg-violet-50 text-violet-600 border-violet-100' },
                              social:       { label: 'ناشط اجتماعي', icon: 'ri-community-line', color: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
                              religious:    { label: 'شخصية دينية', icon: 'ri-quill-pen-line', color: 'bg-amber-50 text-amber-600 border-amber-100' },
                            }[endorsement.specialty];
                            return (
                              <div
                                key={endorsement.id}
                                className="flex gap-3 p-4 rounded-2xl bg-amber-50/50 border border-amber-100"
                              >
                                <img
                                  src={endorsement.avatar}
                                  alt={endorsement.name}
                                  className="w-11 h-11 rounded-xl object-cover object-top shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className="font-bold text-stone-800 text-sm">{endorsement.name}</span>
                                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border flex items-center gap-1 ${specialtyConfig.color}`}>
                                      <i className={`${specialtyConfig.icon} text-[10px]`}></i>
                                      {specialtyConfig.label}
                                    </span>
                                  </div>
                                  <p className="text-xs text-stone-500 mb-2">{endorsement.title}</p>
                                  <p className="text-sm text-stone-700 leading-relaxed italic">
                                    <i className="ri-double-quotes-r text-amber-400 text-base ml-0.5"></i>
                                    {endorsement.quote}
                                  </p>
                                  <p className="text-[10px] text-stone-400 mt-2">{endorsement.date}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'updates' && (
                  <div>
                    {(!campaign.updates || campaign.updates.length === 0) ? (
                      <div className="text-center py-12">
                        <div className="w-14 h-14 flex items-center justify-center bg-stone-100 rounded-2xl mx-auto mb-3">
                          <i className="ri-notification-3-line text-2xl text-stone-400"></i>
                        </div>
                        <p className="text-stone-500 font-semibold text-sm">لا توجد تحديثات بعد</p>
                        <p className="text-stone-400 text-xs mt-1">سيشارك المبدع تحديثات دورية هنا</p>
                      </div>
                    ) : (
                      <div className="space-y-5">
                        {campaign.updates.map((update) => {
                          const typeConfig = {
                            milestone: { icon: 'ri-trophy-fill',         color: 'text-amber-500',   bg: 'bg-amber-50',   label: 'إنجاز' },
                            progress:  { icon: 'ri-bar-chart-fill',      color: 'text-sky-500',     bg: 'bg-sky-50',     label: 'تقدم' },
                            news:      { icon: 'ri-megaphone-fill',      color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'خبر' },
                            thanks:    { icon: 'ri-heart-fill',          color: 'text-rose-500',    bg: 'bg-rose-50',    label: 'شكر' },
                          }[update.type];

                          return (
                            <div key={update.id} className="bg-stone-50 rounded-2xl overflow-hidden border border-stone-100">
                              {/* Update image */}
                              {update.image && (
                                <div className="h-44 overflow-hidden">
                                  <img
                                    src={update.image}
                                    alt={update.title}
                                    className="w-full h-full object-cover object-top"
                                  />
                                </div>
                              )}
                              <div className="p-4">
                                {/* Type badge + date */}
                                <div className="flex items-center justify-between mb-2">
                                  <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${typeConfig.bg} ${typeConfig.color}`}>
                                    <i className={`${typeConfig.icon} text-xs`}></i>
                                    {typeConfig.label}
                                  </span>
                                  <div className="flex items-center gap-1 text-stone-400 text-xs">
                                    <i className="ri-time-line text-xs"></i>
                                    <span>{update.date}</span>
                                  </div>
                                </div>
                                {/* Title */}
                                <h4 className="font-black text-stone-800 text-sm mb-2">{update.title}</h4>
                                {/* Body */}
                                <p className="text-stone-500 text-sm leading-relaxed">{update.body}</p>
                                {/* Creator */}
                                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-stone-200">
                                  <img
                                    src={campaign.creatorAvatar}
                                    alt={campaign.creatorName}
                                    className="w-6 h-6 rounded-full object-cover object-top"
                                  />
                                  <span className="text-xs text-stone-500 font-medium">{campaign.creatorName}</span>
                                  <span className="text-stone-300 text-xs mr-auto">{update.time}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'donors' && (
                  <div className="space-y-4">
                    {mockDonors.map((donor, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-stone-100 shrink-0 flex items-center justify-center">
                          {donor.avatar ? (
                            <img src={donor.avatar} alt={donor.name} className="w-full h-full object-cover object-top" />
                          ) : (
                            <i className="ri-user-line text-stone-400"></i>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-stone-800 text-sm">{donor.name}</span>
                            <span className="font-bold text-emerald-600 text-sm"><SAR amount={donor.amount} iconSize={12} /></span>
                          </div>
                          {donor.message && (
                            <p className="text-stone-500 text-xs mt-0.5">"{donor.message}"</p>
                          )}
                          <span className="text-stone-400 text-xs">{donor.time}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'impact' && campaign.impactReport && (
                  <div>
                    {/* Quick Stats */}
                    <div className="grid grid-cols-3 gap-3 mb-6">
                      {[
                        { icon: 'ri-group-line', label: 'مستفيد', value: `${campaign.impactReport.beneficiariesCount.toLocaleString('en-US')}+`, color: 'text-rose-500' },
                        { icon: 'ri-checkbox-circle-line', label: 'نسبة الإنجاز', value: `${campaign.impactReport.completionRate}%`, color: 'text-emerald-600' },
                        { icon: 'ri-image-line', label: 'صورة وفيديو', value: `${campaign.impactReport.media.length}`, color: 'text-amber-600' },
                      ].map((s) => (
                        <div key={s.label} className="bg-stone-50 rounded-xl p-3 text-center">
                          <div className={`text-lg font-black ${s.color}`}>{s.value}</div>
                          <div className="text-xs text-stone-500 mt-0.5">{s.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Summary */}
                    <p className="text-stone-600 text-sm leading-relaxed mb-5">{campaign.impactReport.summary}</p>

                    {/* Outcomes preview */}
                    <div className="space-y-2 mb-5">
                      {campaign.impactReport.outcomes.slice(0, 3).map((o, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <div className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-100 shrink-0 mt-0.5">
                            <i className="ri-check-line text-emerald-600 text-xs"></i>
                          </div>
                          <span className="text-stone-600 text-sm">{o}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA to full report */}
                    <Link
                      to={`/campaign/${campaign.id}/report`}
                      className="flex items-center justify-center gap-2 w-full bg-emerald-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-bar-chart-2-line"></i>
                      عرض التقرير الكامل
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Related Campaigns */}
            {related.length > 0 && (
              <div>
                <h2 className="text-xl font-black text-stone-800 mb-4">حملات مشابهة</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {related.map((c) => (
                    <CampaignCard key={c.id} campaign={c} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Donation Panel */}
          <div className="w-full lg:w-80 shrink-0">
            <DonationPanel campaign={campaign} />
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CampaignPage;
